import React from "react";
import { Helmet } from "react-helmet-async";

export default function SEO({ page, locations = [], physician = null, settings = {}, siteUrl, lang = "de" }) {
  if (!page) return null;
  const SITE = siteUrl || "https://preventum-diez.de";
  const prefix = lang === "en" ? "/en" : "";
  const url = `${SITE}${prefix}${page.route}`;
  const altDe = `${SITE}${page.route}`;
  const altEn = `${SITE}/en${page.route}`;
  const inLang = lang === "en" ? "en-US" : "de-DE";
  const title = page.seo_title_de || page.h1_de || "Preventum Diez";
  const desc = page.meta_description_de || settings.short_description || "";
  const ogImg = page.og_image_url || settings.logo_url;

  const jsonLd = [
    { "@context": "https://schema.org", "@type": "WebSite", name: settings.site_name || "Preventum Diez", url: SITE },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Start", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: page.h1_de, item: url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": page.schema_type || "MedicalWebPage",
      name: title,
      description: desc,
      url,
      inLanguage: inLang,
    },
  ];

  if (page.page_key === "faq") {
    // FAQPage schema added by page itself with faq items
  }

  if (page.page_key === "dr_kirchner" && physician) {
    jsonLd.push({
      "@context": "https://schema.org",
      "@type": "Physician",
      name: physician.name,
      medicalSpecialty: physician.specialty,
      url: `${SITE}/dr-kirchner/`,
    });
  }

  if (["standorte", "kardiologie_diez", "kontakt", "home"].includes(page.page_key)) {
    locations.forEach((loc) => {
      jsonLd.push({
        "@context": "https://schema.org",
        "@type": "MedicalClinic",
        name: loc.name,
        address: {
          "@type": "PostalAddress",
          streetAddress: loc.street,
          postalCode: loc.zip,
          addressLocality: loc.city,
          addressCountry: loc.country || "DE",
        },
        telephone: loc.phone || undefined,
        email: loc.email || undefined,
      });
    });
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="de" href={altDe} />
      <link rel="alternate" hrefLang="en" href={altEn} />
      <link rel="alternate" hrefLang="x-default" href={altDe} />
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content={lang === "en" ? "en_US" : "de_DE"} />
      {ogImg && <meta property="og:image" content={ogImg} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      <html lang={lang} />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}
